import React, { useState, useEffect } from 'react';
import apiCall from '../helper/apiCall';
import { toast } from 'react-toastify';

const VPS_BASE_URL = "https://admin.kanthasri.com"; // Ensure this is defined

const ArtistForm = ({ initialData, onSave, onClose }) => {
    const [states, setStates] = useState([]);
    const [countries, setCountries] = useState([]);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        profile_picture: '',
        mobile: '',
        email: '',
        address: {
            address_line_1: '',
            address_line_2: '',
            city: '',
            pincode: '',
            state_id: '',
            country_id: ''
        }
    });

    useEffect(() => {
        const fetchGeoData = async () => {
            try {
                const [stateRes, countryRes] = await Promise.all([
                    apiCall('getStates'),
                    apiCall('getCountries')
                ]); if (stateRes.success) setStates(stateRes.data);
                if (countryRes.success) setCountries(countryRes.data);
            } catch (error) { toast.error("Error loading location data."); }
        };
        fetchGeoData();
        if (initialData) setFormData(initialData);
    }, [initialData]);

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setLoading(true);
        const uploadData = new FormData();
        // Using artist name or fallback for the folder/filename on VPS
        const uploadName = formData.name || 'artist-profile';
        uploadData.append('name', uploadName);
        uploadData.append('image', file);

        try {
            
            const response = await fetch(`${VPS_BASE_URL}/admin/api/v1/artist/image/upload`, {
                method: 'POST',
                body: uploadData,
            });

            const result = await response.json();

            if (result.success) {
                // Assuming result.images.main or result.images.grid contains the path
                const imageUrl = `${VPS_BASE_URL}${result?.images?.thumbnail}`;
                console.log('imageUrl:',imageUrl);
                setFormData(prev => ({ 
                        ...prev, 
                        profile_picture: result.images // result.images is the {thumbnail, main} object
                    }));                
                    toast.success("Image uploaded successfully!");
            } else {
                toast.error("Upload failed: " + (result.message || "Unknown error"));
            }
        } catch (error) {
            console.error("VPS Upload Error:", error);
            toast.error("Network error: Could not reach VPS.");
        } finally {
            setLoading(false);
            e.target.value = null;
        }
    };

    const handleArtistChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleAddressChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            address: { ...prev.address, [name]: value }
        }));
    };

    return (
        <form onSubmit={(e) => { e.preventDefault(); onSave(formData); }} className="space-y-4 max-h-[80vh] overflow-y-auto px-2">
            <h3 className="font-bold text-gray-800 border-b pb-1 text-sm uppercase tracking-wider">Profile Details</h3>

            <div className="flex flex-col items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50">
                {formData.profile_picture ? (
                    <div className="relative">
                        
                        <img src={`${VPS_BASE_URL}${formData.profile_picture.thumbnail}`} alt="Profile" className="h-24 w-24 rounded-full object-cover border-2 border-indigo-500" />
                        <button
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, profile_picture: '' }))}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 shadow-lg hover:bg-red-600"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                        </button>
                    </div>
                ) : (
                    <label className="cursor-pointer flex flex-col items-center">
                        {loading ? (
                            <svg className="animate-spin h-10 w-10 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.963l3-2.672z"></path>
                            </svg>
                        ) : (
                            <div className="text-gray-500 hover:text-indigo-600 text-center">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                <p className="text-xs mt-2">Upload Profile Picture</p>
                            </div>
                        )}
                        <input type="file" className="hidden" accept="image/*" onChange={handleImageUpload} disabled={loading} />
                    </label>
                )}
            </div>

            {/* Standard Fields */}
            <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700">Artist Name</label>
                    <input type="text" name="name" required value={formData.name} onChange={handleArtistChange} className="mt-1 block w-full border border-gray-300 rounded-md p-2" />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700">Mobile</label>
                    <input type="tel" name="mobile" required value={formData.mobile} onChange={handleArtistChange} className="mt-1 block w-full border border-gray-300 rounded-md p-2" />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700">Email</label>
                    <input type="email" name="email" required value={formData.email} onChange={handleArtistChange} className="mt-1 block w-full border border-gray-300 rounded-md p-2" />
                </div>
            </div>

            <h3 className="font-bold text-gray-800 border-b pb-1 mt-6 text-sm uppercase tracking-wider">Address Details</h3>
            <div className="grid grid-cols-2 gap-4 pb-10">
                <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700">Address Line 1</label>
                    <input type="text" name="address_line_1" value={formData.address?.address_line_1} onChange={handleAddressChange} className="mt-1 block w-full border border-gray-300 rounded-md p-2" />
                </div>
                <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700">Address Line 2</label>
                    <input type="text" name="address_line_2" value={formData.address?.address_line_2} onChange={handleAddressChange} className="mt-1 block w-full border border-gray-300 rounded-md p-2" />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700">City</label>
                    <input type="text" name="city" value={formData.address?.city} onChange={handleAddressChange} className="mt-1 block w-full border border-gray-300 rounded-md p-2" />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700">Pincode</label>
                    <input type="text" name="pincode" maxLength="6" value={formData.address?.pincode} onChange={handleAddressChange} className="mt-1 block w-full border border-gray-300 rounded-md p-2" />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700">State</label>
                    <select name="state_id" value={formData.address?.state_id} onChange={handleAddressChange} className="mt-1 block w-full border border-gray-300 rounded-md p-2 bg-white">
                        <option value="">Select State</option>
                        {states.map(s => <option key={s.state_id} value={s.state_id}>{s.state_name}</option>)}
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700">Country</label>
                    <select name="country_id" value={formData.address?.country_id} onChange={handleAddressChange} className="mt-1 block w-full border border-gray-300 rounded-md p-2 bg-white">
                        <option value="">Select Country</option>
                        {countries.map(c => <option key={c.country_id} value={c.country_id}>{c.country_name}</option>)}
                    </select>
                </div>

            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t sticky bottom-0 bg-white">
                <button type="button" onClick={onClose} className="bg-gray-200 px-4 py-2 rounded-md font-medium text-gray-700">Cancel</button>
                <button type="submit" disabled={loading} className={`bg-indigo-600 text-white px-4 py-2 rounded-md font-medium shadow-lg ${loading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-indigo-700'}`}>
                    {initialData ? 'Update Artist' : 'Save Artist'}
                </button>
            </div>
        </form>
    );
};

export default ArtistForm;